import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { liberacaoService, pacienteService } from '../services';
import useDebounce from '../hooks/useDebounce';
import SeletorPacientes from '../components/SeletorPacientes';
import SeletorMedicamentos from '../components/SeletorMedicamentos';
import SeletorMedicamentosEstoque from '../components/SeletorMedicamentosEstoque';
import SeletorLotes from '../components/SeletorLotes';
import { 
  Save, 
  ArrowLeft, 
  AlertTriangle,
  Trash2,
  User,
  Package,
  Search,
  Lock,
  FileText
} from 'lucide-react';

const NovaLiberacao = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [step, setStep] = useState(1); // 1: Selecionar paciente, 2: Adicionar medicamentos, 3: Confirmar com senha
  
  // Estados para o formulário
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [observacoes, setObservacoes] = useState('');
  const [numeroProcesso, setNumeroProcesso] = useState('');
  const [itens, setItens] = useState([]);
  const [senha, setSenha] = useState('');
  const [tentativasSenha, setTentativasSenha] = useState(0);
  const maxTentativas = 3;
  
  // Estados para busca
  const [buscaPaciente, setBuscaPaciente] = useState('');
  const [pacientesEncontrados, setPacientesEncontrados] = useState([]);
  const [buscaMedicamento, setBuscaMedicamento] = useState('');
  const [medicamentosEncontrados, setMedicamentosEncontrados] = useState([]);
  const [showPacienteSearch, setShowPacienteSearch] = useState(false);
  const [showMedicamentoSearch, setShowMedicamentoSearch] = useState(false);
  const [showModalPacientes, setShowModalPacientes] = useState(false);
  const [showModalMedicamentos, setShowModalMedicamentos] = useState(false);
  const [showModalMedicamentosEstoque, setShowModalMedicamentosEstoque] = useState(false);
  const [medicamentosVinculados, setMedicamentosVinculados] = useState([]);
  const [loadingMedicamentosVinculados, setLoadingMedicamentosVinculados] = useState(false);
  
  // Estados para seleção de lotes
  const [showSeletorLotes, setShowSeletorLotes] = useState(false);
  const [medicamentoParaLote, setMedicamentoParaLote] = useState(null);

  // Aplicar debounce nas buscas com 300ms de delay
  const debouncedBuscaPaciente = useDebounce(buscaPaciente, 300);
  const debouncedBuscaMedicamento = useDebounce(buscaMedicamento, 300);

  // Buscar pacientes
  const buscarPacientes = async (termo) => {
    if (termo.length < 2) {
      setPacientesEncontrados([]);
      return;
    }
    
    try {
      const response = await pacienteService.getAll({ search: termo, limit: 10 });
      setPacientesEncontrados(response.pacientes || []);
    } catch (error) {
      console.error('Erro ao buscar pacientes:', error);
    }
  };

  // Carregar medicamentos vinculados quando um paciente é selecionado
  const carregarMedicamentosVinculados = async (pacienteId) => {
    try {
      setLoadingMedicamentosVinculados(true);
      const response = await liberacaoService.getMedicamentosVinculadosPaciente(pacienteId);
      setMedicamentosVinculados(response.medicamentosVinculados || []);
    } catch (error) {
      console.error('Erro ao carregar medicamentos vinculados:', error);
      setMessage({ 
        type: 'error', 
        text: 'Erro ao carregar medicamentos vinculados ao paciente' 
      });
      setMedicamentosVinculados([]);
    } finally {
      setLoadingMedicamentosVinculados(false);
    }
  };

  const buscarMedicamentosVinculados = async (termo) => {
    if (!pacienteSelecionado) {
      setMedicamentosEncontrados([]);
      return;
    }
    
    if (termo.length < 2) {
      setMedicamentosEncontrados([]);
      return;
    }
    
    try {
      // Filtrar dos medicamentos vinculados já carregados
      const filtrados = medicamentosVinculados.filter(vinculacao => 
        vinculacao.medicamentoRegistrado.nome.toLowerCase().includes(termo.toLowerCase()) ||
        vinculacao.medicamentoRegistrado.principioAtivo.toLowerCase().includes(termo.toLowerCase())
      );
      
      setMedicamentosEncontrados(filtrados);
    } catch (error) {
      console.error('Erro ao filtrar medicamentos vinculados:', error);
    }
  };

  useEffect(() => {
    if (debouncedBuscaPaciente) {
      buscarPacientes(debouncedBuscaPaciente);
    } else {
      setPacientesEncontrados([]);
    }
  }, [debouncedBuscaPaciente]);

  useEffect(() => {
    if (debouncedBuscaMedicamento && pacienteSelecionado) {
      buscarMedicamentosVinculados(debouncedBuscaMedicamento);
    } else {
      setMedicamentosEncontrados([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBuscaMedicamento, pacienteSelecionado, medicamentosVinculados]);

  const selecionarPaciente = (paciente) => {
    setPacienteSelecionado(paciente);
    setBuscaPaciente(paciente.nome);
    setShowPacienteSearch(false);
    setPacientesEncontrados([]);
    // Carregar medicamentos vinculados ao paciente
    carregarMedicamentosVinculados(paciente.id);
  };

  const selecionarPacienteModal = (paciente) => {
    setPacienteSelecionado(paciente);
    setBuscaPaciente(paciente.nome);
    setShowPacienteSearch(false);
    setPacientesEncontrados([]);
    // Carregar medicamentos vinculados ao paciente
    carregarMedicamentosVinculados(paciente.id);
  };

  const selecionarMedicamentoModal = (medicamento) => {
    // Em vez de adicionar diretamente, abrir seletor de lotes
    setMedicamentoParaLote(medicamento);
    setShowSeletorLotes(true);
    // Pode ter vindo de um dos dois modais; fechamos ambos para garantir
    setShowModalMedicamentos(false);
    setShowModalMedicamentosEstoque(false);
  };

  const selecionarLote = (vinculacao, lote) => {
    // Adicionar item com o lote específico selecionado
    adicionarItemComLote(vinculacao, lote);
  };

  const adicionarItemComLote = (vinculacao, lote) => {
    const medicamento = vinculacao.medicamentoRegistrado;
    const itemExistente = itens.find(item => 
      item.medicamentoRegistradoId === medicamento.id && 
      item.loteId === lote.id
    );
    
    if (itemExistente) {
      setMessage({ type: 'error', text: 'Este medicamento com esse lote já foi adicionado' });
      return;
    }

    const novoItem = {
      medicamentoRegistradoId: medicamento.id,
      vinculacaoId: vinculacao.id,
      loteId: lote.id,
      medicamento: {
        id: medicamento.id,
        nome: medicamento.nome,
        principioAtivo: medicamento.principioAtivo,
        dosagem: medicamento.dosagem,
        formaFarmaceutica: medicamento.formaFarmaceutica
      },
      vinculacao: {
        quantidadeMensal: vinculacao.quantidade_mensal,
        dosagem_prescrita: vinculacao.dosagem_prescrita,
        observacoes: vinculacao.observacoes
      },
      // Se o medicamento não é vinculado ao paciente (veio do estoque geral), começa com 1 unidade;
      // caso contrário, usa o mínimo entre quantidade mensal e estoque
      quantidade: vinculacao.quantidade_mensal ? Math.min(vinculacao.quantidade_mensal, lote.estoque) : 1,
      lote: lote.lote,
      dataValidade: lote.dataValidade,
      estoqueDisponivel: lote.estoque,
      fabricante: lote.fabricante,
      observacoes: ''
    };
    
    setItens([...itens, novoItem]);
    setBuscaMedicamento('');
    setShowMedicamentoSearch(false);
    setMedicamentosEncontrados([]);
    setMessage({ type: 'success', text: `Medicamento ${medicamento.nome} (Lote: ${lote.lote}) adicionado` });
  };

  const adicionarItem = (vinculacao) => {
    // Em vez de adicionar diretamente, abrir seletor de lotes
    setMedicamentoParaLote(vinculacao);
    setShowSeletorLotes(true);
  };

  const atualizarItem = (index, campo, valor) => {
    const novosItens = [...itens];
    novosItens[index][campo] = valor;
    setItens(novosItens);
  };

  const removerItem = (index) => {
    const novosItens = itens.filter((_, i) => i !== index);
    setItens(novosItens);
  };

  const validarFormulario = () => {
    if (step === 1) {
      if (!pacienteSelecionado) {
        setMessage({ type: 'error', text: 'Selecione um paciente' });
        return false;
      }
    } else if (step === 2) {
      if (itens.length === 0) {
        setMessage({ type: 'error', text: 'Adicione pelo menos um medicamento' });
        return false;
      }
      
      for (const item of itens) {
        if (!item.quantidade || item.quantidade <= 0) {
          setMessage({ type: 'error', text: 'Todas as quantidades devem ser maiores que zero' });
          return false;
        }

        const estoqueDisponivel = item.estoqueDisponivel || 0;
        if (estoqueDisponivel <= 0) {
          setMessage({ type: 'error', text: `Não há estoque disponível para ${item.medicamento.nome}` });
          return false;
        }

        if (item.quantidade > estoqueDisponivel) {
          setMessage({ type: 'error', text: `Quantidade não pode exceder ${estoqueDisponivel} (estoque disponível) para ${item.medicamento.nome}` });
          return false;
        }
      }
    } else if (step === 3) {
      // Apenas atendentes precisam inserir senha
      if (user?.cargo === 'atendente' && !senha) {
        setMessage({ type: 'error', text: 'Digite sua senha de liberação para confirmar a liberação' });
        return false;
      }
    }
    
    return true;
  };

  const proximoPasso = () => {
    if (validarFormulario()) {
      setMessage({ type: '', text: '' });
      setStep(step + 1);
    }
  };

  const passoAnterior = () => {
    setMessage({ type: '', text: '' });
    setStep(step - 1);
  };

  const confirmarLiberacao = async () => {
    if (!validarFormulario()) return;
    
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      
      // Para admin e farmacêuticos, enviar "0" como senha. Para atendentes, usar a senha inserida
      const senhaLiberacao = (user?.cargo === 'admin' || user?.cargo === 'farmaceutico') ? '0' : senha;
      
      // Criar a liberação com senha de liberação
      const dadosLiberacao = {
        pacienteId: pacienteSelecionado.id,
        observacoes,
        numeroProcesso: numeroProcesso || null,
        senhaLiberacao: senhaLiberacao,
        itens: itens.map(item => ({
          medicamentoRegistradoId: item.medicamentoRegistradoId,
          // Só envia vinculacaoId se for um id numérico real (vinculado ao paciente)
          vinculacaoId: typeof item.vinculacaoId === 'number' ? item.vinculacaoId : null,
          loteId: item.loteId,
          quantidade: parseInt(item.quantidade, 10),
          observacoes: item.observacoes || null
        }))
      };
      
      const liberacao = await liberacaoService.create(dadosLiberacao);
      
      setMessage({ type: 'success', text: `Liberação criada e confirmada! Número: ${liberacao.numeroRecibo}` });
      
      setTimeout(() => {
        navigate(`/liberacoes/detalhes/${liberacao.id}`);
      }, 2000);
      
    } catch (error) {
      console.error('Erro ao criar liberação:', error);
      
      if (error.response?.status === 401 && error.response?.data?.message?.includes('Senha de liberação incorreta')) {
        const novasTentativas = tentativasSenha + 1;
        setTentativasSenha(novasTentativas);
        
        if (novasTentativas >= maxTentativas) {
          setMessage({ 
            type: 'error', 
            text: 'Máximo de tentativas excedido. Você será desconectado por segurança.' 
          });
          
          setTimeout(() => {
            logout();
          }, 2000);
        } else {
          const tentativasRestantes = maxTentativas - novasTentativas;
          setMessage({ 
            type: 'error', 
            text: `Senha de liberação incorreta. Você tem mais ${tentativasRestantes} tentativa${tentativasRestantes !== 1 ? 's' : ''}.` 
          });
          setSenha('');
        }
      } else if (error.response?.data?.message) {
        setMessage({ type: 'error', text: error.response.data.message });
      } else {
        setMessage({ type: 'error', text: 'Erro ao criar liberação' });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatarCpf = (cpf) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={32} />
            Nova Liberação
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Passo {step} de 3 - {step === 1 ? 'Selecionar Paciente' : step === 2 ? 'Adicionar Medicamentos' : 'Confirmar Liberação'}
          </p>
        </div>
        
        <button 
          type="button"
          onClick={() => navigate('/liberacoes')}
          className="btn btn-secondary"
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.text}
        </div>
      )}

      {/* Indicador de progresso */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px'
      }}>
        {[1, 2, 3].map((numero) => (
          <div key={numero} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: step >= numero ? '#007bff' : '#dee2e6',
              color: step >= numero ? 'white' : '#6c757d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              {numero}
            </div>
            {numero < 3 && (
              <div style={{
                width: '60px',
                height: '2px',
                backgroundColor: step > numero ? '#007bff' : '#dee2e6',
                margin: '0 10px'
              }}></div>
            )}
          </div>
        ))}
      </div>

      <div className="card">
        {/* Passo 1: Selecionar Paciente */}
        {step === 1 && (
          <div>
            <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <User size={24} />
              Selecionar Paciente
            </h3>
            
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <label className="form-label">Buscar Paciente</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={20} style={{ 
                    position: 'absolute', 
                    left: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#667'
                  }} />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Digite o nome ou CPF do paciente"
                    value={buscaPaciente}
                    onChange={(e) => setBuscaPaciente(e.target.value)}
                    onFocus={() => setShowPacienteSearch(true)}
                    style={{ paddingLeft: '40px' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowModalPacientes(true)}
                  className="btn btn-secondary"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <User size={18} />
                  Selecionar da Lista
                </button>
              </div>
              
              {showPacienteSearch && pacientesEncontrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {pacientesEncontrados.map((paciente) => (
                    <div
                      key={paciente.id}
                      onClick={() => selecionarPaciente(paciente)}
                      style={{
                        padding: '10px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div>
                        <strong>{paciente.nome}</strong>
                        <div style={{ fontSize: '12px', color: '#667' }}>
                          CPF: {formatarCpf(paciente.cpf)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pacienteSelecionado && (
              <div style={{
                padding: '15px',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Paciente Selecionado</h4>
                <p><strong>Nome:</strong> {pacienteSelecionado.nome}</p>
                <p><strong>CPF:</strong> {formatarCpf(pacienteSelecionado.cpf)}</p>
                {pacienteSelecionado.cartaoSus && (
                  <p><strong>Cartão SUS:</strong> {pacienteSelecionado.cartaoSus}</p>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Número do Processo</label>
              <input
                type="text"
                className="form-control"
                placeholder="Número do processo (opcional)"
                value={numeroProcesso}
                onChange={(e) => setNumeroProcesso(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Observações</label>
              <textarea
                className="form-control"
                placeholder="Observações sobre a liberação"
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows="3"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
              <button 
                onClick={proximoPasso}
                className="btn btn-primary"
                disabled={!pacienteSelecionado}
              >
                Próximo
                <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        )}

        {/* Passo 2: Adicionar Medicamentos */}
        {step === 2 && (
          <div>
            <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Package size={24} />
              Adicionar Medicamentos
            </h3>

            {/* Exibir medicamentos vinculados disponíveis */}
            {pacienteSelecionado && (
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Package size={18} />
                  Medicamentos Vinculados ao Paciente
                  {loadingMedicamentosVinculados && (
                    <div className="spinner" style={{ width: '16px', height: '16px', marginLeft: '10px' }}></div>
                  )}
                </h4>
                
                {medicamentosVinculados.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '15px',
                    marginTop: '15px'
                  }}>
                    {medicamentosVinculados.map((vinculacao) => (
                      <div
                        key={vinculacao.id}
                        style={{
                          border: `2px solid ${vinculacao.ativo ? '#28a745' : '#dc3545'}`,
                          borderRadius: '8px',
                          padding: '15px',
                          backgroundColor: vinculacao.ativo ? '#f8fff8' : '#fff5f5',
                          cursor: vinculacao.ativo ? 'pointer' : 'not-allowed',
                          opacity: vinculacao.ativo ? 1 : 0.6,
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => vinculacao.ativo ? adicionarItem(vinculacao) : null}
                        onMouseEnter={(e) => {
                          if (vinculacao.ativo) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (vinculacao.ativo) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <h5 style={{ 
                            margin: '0', 
                            color: vinculacao.ativo ? '#155724' : '#721c24',
                            fontSize: '16px' 
                          }}>
                            {vinculacao.medicamentoRegistrado.nome}
                          </h5>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: vinculacao.ativo ? '#28a745' : '#dc3545',
                            color: 'white'
                          }}>
                            {vinculacao.ativo ? 'ATIVO' : 'INATIVO'}
                          </span>
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#667', marginBottom: '8px' }}>
                          <strong>Princípio Ativo:</strong> {vinculacao.medicamentoRegistrado.principioAtivo}
                        </div>
                        
                        <div style={{ fontSize: '14px', color: '#667', marginBottom: '8px' }}>
                          <strong>Dosagem:</strong> {vinculacao.medicamentoRegistrado.dosagem}
                        </div>
                        
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          fontSize: '13px',
                          color: '#333',
                          marginTop: '10px'
                        }}>
                          <div>
                            <strong>Qtd. Mensal:</strong> {vinculacao.quantidade_mensal}
                          </div>
                          <div>
                            <strong>Prescrição:</strong> {vinculacao.dosagem_prescrita || 'N/A'}
                          </div>
                        </div>
                        
                        {vinculacao.observacoes && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#667', 
                            fontStyle: 'italic',
                            marginTop: '8px',
                            padding: '5px',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            borderRadius: '4px'
                          }}>
                            {vinculacao.observacoes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : !loadingMedicamentosVinculados ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px dashed #dee2e6',
                    marginTop: '15px'
                  }}>
                    <Package size={32} color="#6c757d" style={{ marginBottom: '10px' }} />
                    <p style={{ color: '#6c757d', margin: 0 }}>
                      Nenhum medicamento vinculado encontrado para este paciente.
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            <div style={{ position: 'relative', marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label className="form-label" style={{ margin: 0 }}>Buscar Medicamento</label>
                <button
                  type="button"
                  onClick={() => setShowModalMedicamentosEstoque(true)}
                  className="btn btn-outline-primary btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <Package size={16} />
                  Selecionar da Lista
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{ 
                  position: 'absolute', 
                  left: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#667'
                }} />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome do medicamento"
                  value={buscaMedicamento}
                  onChange={(e) => setBuscaMedicamento(e.target.value)}
                  onFocus={() => setShowMedicamentoSearch(true)}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              
              {showMedicamentoSearch && medicamentosEncontrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {medicamentosEncontrados.map((vinculacao) => {
                    const medicamento = vinculacao.medicamentoRegistrado;
                    const podeSelecionar = vinculacao.ativo;
                    
                    return (
                      <div
                        key={vinculacao.id}
                        onClick={() => podeSelecionar ? adicionarItem(vinculacao) : null}
                        style={{
                          padding: '10px',
                          borderBottom: '1px solid #eee',
                          cursor: podeSelecionar ? 'pointer' : 'not-allowed',
                          backgroundColor: !vinculacao.ativo ? '#f8d7da' : 'white',
                          opacity: podeSelecionar ? 1 : 0.6
                        }}
                        onMouseEnter={(e) => {
                          if (podeSelecionar) {
                            e.target.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = !vinculacao.ativo ? '#f8d7da' : 'white';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong style={{ color: !vinculacao.ativo ? '#721c24' : 'inherit' }}>
                              {medicamento.nome}
                              {!vinculacao.ativo && <span style={{ color: '#dc3545', marginLeft: '8px' }}>(INATIVO)</span>}
                            </strong>
                            <div style={{ fontSize: '12px', color: '#667' }}>
                              {medicamento.principioAtivo} - Dosagem: {medicamento.dosagem}
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              Quantidade mensal: {vinculacao.quantidade_mensal} | 
                              Prescrição: {vinculacao.dosagem_prescrita || 'Não informada'}
                            </div>
                          </div>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            backgroundColor: vinculacao.ativo ? '#d4edda' : '#f8d7da',
                            color: vinculacao.ativo ? '#155724' : '#721c24'
                          }}>
                            {vinculacao.ativo ? 'Vinculado' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Lista de itens adicionados */}
            {itens.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '15px' }}>Medicamentos Selecionados</h4>
                {itens.map((item, index) => (
                  <div key={index} style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '15px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div>
                        <h5 style={{ margin: '0 0 5px 0' }}>{item.medicamento.nome}</h5>
                        <p style={{ margin: '0', color: '#667', fontSize: '14px' }}>
                          {item.medicamento.principioAtivo} - Dosagem: {item.medicamento.dosagem}
                        </p>
                        {item.lote && (
                          <div style={{ 
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#e7f3ff',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            Lote Selecionado: {item.lote} | Validade: {item.dataValidade ? new Date(item.dataValidade).toLocaleDateString('pt-BR') : 'N/A'} | Estoque: {item.estoqueDisponivel}
                            {item.fabricante && <span> | Fabricante: {item.fabricante}</span>}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removerItem(index)}
                        className="btn btn-danger"
                        style={{ padding: '5px 10px' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '15px'
                    }}>
                      <div className="form-group">
                        <label className="form-label">Quantidade</label>
                        <input
                          type="number"
                          min="1"
                          max={item.estoqueDisponivel || 0}
                          value={item.quantidade}
                          onChange={(e) => atualizarItem(index, 'quantidade', e.target.value)}
                          className="form-control"
                        />
                        {item.estoqueDisponivel && (
                          <small style={{ color: '#667', fontSize: '11px' }}>
                            Máximo: {item.estoqueDisponivel} (estoque disponível)
                          </small>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Lote Selecionado</label>
                        <input
                          type="text"
                          value={item.lote || 'Não selecionado'}
                          className="form-control"
                          placeholder="Lote do medicamento"
                          disabled
                          readOnly
                          style={{ 
                            backgroundColor: item.lote ? '#e7f3ff' : '#f8f9fa', 
                            color: item.lote ? '#0066cc' : '#6c757d',
                            fontWeight: item.lote ? 'bold' : 'normal',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Validade do Lote</label>
                        <input
                          type="text"
                          value={item.dataValidade ? new Date(item.dataValidade).toLocaleDateString('pt-BR') : 'Não informado'}
                          className="form-control"
                          disabled
                          readOnly
                          style={{ 
                            backgroundColor: item.dataValidade ? '#e7f3ff' : '#f8f9fa', 
                            color: item.dataValidade ? '#0066cc' : '#6c757d',
                            fontWeight: item.dataValidade ? 'bold' : 'normal',
                            cursor: 'not-allowed'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '15px' }}>
                      <label className="form-label">Observações do Item</label>
                      <input
                        type="text"
                        value={item.observacoes}
                        onChange={(e) => atualizarItem(index, 'observacoes', e.target.value)}
                        className="form-control"
                        placeholder="Observações específicas para este item"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
              <button 
                onClick={passoAnterior}
                className="btn btn-secondary"
              >
                <ArrowLeft size={20} />
                Anterior
              </button>
              
              <button 
                onClick={proximoPasso}
                className="btn btn-primary"
                disabled={itens.length === 0}
              >
                Próximo
                <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>
          </div>
        )}

        {/* Passo 3: Confirmar com Senha */}
        {step === 3 && (
          <div>
            <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Lock size={24} />
              Confirmar Liberação
            </h3>

            {/* Resumo da liberação */}
            <div style={{ marginBottom: '30px' }}>
              <h4>Resumo da Liberação</h4>
              
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <p><strong>Paciente:</strong> {pacienteSelecionado?.nome}</p>
                <p><strong>CPF:</strong> {formatarCpf(pacienteSelecionado?.cpf)}</p>
                <p><strong>Total de Itens:</strong> {itens.length}</p>
                <p><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
                <p><strong>Responsável:</strong> {user?.nome}</p>
                {numeroProcesso && <p><strong>Número do Processo:</strong> {numeroProcesso}</p>}
                {observacoes && <p><strong>Observações:</strong> {observacoes}</p>}
              </div>

              <h5>Medicamentos:</h5>
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Medicamento</th>
                      <th>Quantidade</th>
                      <th>Lote</th>
                      <th>Validade</th>
                      <th>Fabricante</th>
                      <th>Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.medicamento.nome}</strong>
                          <br />
                          <small style={{ color: '#667' }}>
                            {item.medicamento.principioAtivo}
                          </small>
                        </td>
                        <td>
                          <div>
                            <strong>{item.quantidade}</strong>
                            {item.estoqueDisponivel && (
                              <>
                                <br />
                                <small style={{ color: '#667' }}>
                                  de {item.estoqueDisponivel} disponível
                                </small>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold',
                            color: item.lote ? '#0066cc' : '#999'
                          }}>
                            {item.lote || 'Não informado'}
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold',
                            color: item.dataValidade ? '#0066cc' : '#999'
                          }}>
                            {item.dataValidade 
                              ? new Date(item.dataValidade).toLocaleDateString('pt-BR')
                              : 'Não informado'
                            }
                          </span>
                        </td>
                        <td>
                          <span style={{ color: '#667' }}>
                            {item.fabricante || 'Não informado'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '12px', color: '#667' }}>
                            {item.observacoes || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Campo de senha apenas para atendentes */}
            {/* Debug: {JSON.stringify({cargo: user?.cargo, tipo: typeof user?.cargo})} */}
            {user?.cargo === 'atendente' ? (
              <div className="form-group">
                <label className="form-label required">
                  <Lock size={16} />
                  Digite sua senha de liberação para confirmar a liberação
                </label>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="form-control"
                  placeholder="Sua senha de liberação"
                  required
                  disabled={loading || tentativasSenha >= maxTentativas}
                />
                {tentativasSenha > 0 && tentativasSenha < maxTentativas && (
                  <small style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    ⚠️ Tentativa {tentativasSenha} de {maxTentativas}
                  </small>
                )}
                {tentativasSenha >= maxTentativas && (
                  <small style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    🚫 Máximo de tentativas atingido
                  </small>
                )}
              </div>
            ) : (user?.cargo === 'admin' || user?.cargo === 'farmaceutico') ? (
              <div style={{
                padding: '15px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #28a745',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  color: '#155724'
                }}>
                  <Lock size={20} />
                  <div>
                    <strong>Autorização Automática</strong>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                      Como {user?.cargo === 'admin' ? 'administrador' : 'farmacêutico'}, você possui autorização para criar liberações sem senha adicional.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label required">
                  <Lock size={16} />
                  Digite sua senha de liberação para confirmar a liberação
                </label>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="form-control"
                  placeholder="Sua senha de liberação"
                  required
                  disabled={loading || tentativasSenha >= maxTentativas}
                />
                {tentativasSenha > 0 && tentativasSenha < maxTentativas && (
                  <small style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    ⚠️ Tentativa {tentativasSenha} de {maxTentativas}
                  </small>
                )}
                {tentativasSenha >= maxTentativas && (
                  <small style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    🚫 Máximo de tentativas atingido
                  </small>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
              <button 
                onClick={passoAnterior}
                className="btn btn-secondary"
                disabled={loading}
              >
                <ArrowLeft size={20} />
                Anterior
              </button>
              
              <button 
                onClick={confirmarLiberacao}
                className="btn btn-primary"
                disabled={loading || (user?.cargo === 'atendente' && (!senha || tentativasSenha >= maxTentativas))}
              >
                {loading ? (
                  <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                ) : (
                  <>
                    <Save size={20} />
                    Confirmar Liberação
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Seleção de Pacientes */}
      <SeletorPacientes
        isOpen={showModalPacientes}
        onClose={() => setShowModalPacientes(false)}
        onSelect={selecionarPacienteModal}
        pacienteSelecionado={pacienteSelecionado}
      />

      {/* Modal de Seleção de Medicamentos */}
      <SeletorMedicamentos
        isOpen={showModalMedicamentos}
        onClose={() => setShowModalMedicamentos(false)}
        onSelect={selecionarMedicamentoModal}
        medicamentosSelecionados={itens}
      />

      {/* Modal de Seleção de Medicamentos do Estoque */}
      <SeletorMedicamentosEstoque
        isOpen={showModalMedicamentosEstoque}
        onClose={() => setShowModalMedicamentosEstoque(false)}
        onSelect={selecionarMedicamentoModal}
        medicamentosSelecionados={itens}
      />

      {/* Modal de Seleção de Lotes */}
      <SeletorLotes
        isOpen={showSeletorLotes}
        onClose={() => setShowSeletorLotes(false)}
        onSelect={selecionarLote}
        medicamento={medicamentoParaLote}
      />
    </div>
  );
};

export default NovaLiberacao;